import {
  ENABLEMENT_PROVIDER_SLUGS,
  partnerEnablementPublicRoutePrefix,
} from '@/integrations/enablement/providers';

/**
 * Routes that must never receive a locale prefix (e.g. `/en/`).
 *
 * FHIR / SMART-on-FHIR launch URLs are registered in EHR systems with exact
 * paths. Adding a locale segment breaks the OAuth redirect_uri match and
 * prevents the auth handshake from completing.
 */
export const LOCALE_EXEMPT_ROUTES = ['/integration/'];

export const PUBLIC_ROUTES = [
  // Hosted onboarding page: Auth0 redirects here mid-login with no session, so
  // it must never be bounced to the login gate. Exact-path match, like
  // `/auth/sso-handoff` below.
  '/auth/onboard',
  // Back-button landing once that form has posted to Auth0; static, no token.
  '/auth/onboard-complete',
  // Where middleware rewrites a visit whose redirect token failed; static, no token.
  '/auth/onboard-expired',
  // Public, pre-auth entry for native-to-web SSO handoff (APP-8227). Exact-
  // path match only: `isPublicRoute` in `middleware.ts` treats a no-trailing-
  // slash entry as `route` OR `${route}/...`, so a sibling route such as
  // `/auth/sso-handoff-extra` does NOT become public. A locale-prefixed
  // request (e.g. `/en/auth/sso-handoff`) matches middleware's locale catch-
  // all even though the bare path is not itself in `config.matcher` — listed
  // here defensively so it can never be bounced to the Kinde login gate,
  // mirroring the `/onprem/login-callback` precedent below.
  '/auth/sso-handoff',
  // Dictate marketing download entry: decides sign-up vs straight into the app,
  // so it must render for signed-out visitors.
  '/dictate/get-started',
  '/chat',
  // FHIR / EHR integration routes bypass Kinde auth. These pages either use
  // their own FHIR auth flow or handle authentication server-side. The FHIR
  // cookie is set in getServerSideProps (response), so it isn't on the incoming
  // request when middleware runs — listing them here prevents a Kinde redirect.
  '/integration/epic',
  // Encounter-based EHR launch. Client-only: the page renders its own
  // SignInModal for a signed-out user, so middleware must not bounce it to the
  // Kinde gate first (same reason `/integration/onboarding` is listed).
  '/integration/appointment-launch',
  // Unauthenticated error page for failed backend EHR launches (INT-5101). The
  // backend redirects here with the error in the query params before any Heidi
  // session exists, so it must bypass Kinde auth.
  '/integration/launch/error',
  '/integration/medtech',
  '/integration/onboarding',
  // Partner App Market enablement (+ launch under the same prefix). Derived from
  // ENABLEMENT_PROVIDERS so a new slug cannot miss the pre-auth public entry.
  ...ENABLEMENT_PROVIDER_SLUGS.map(partnerEnablementPublicRoutePrefix),
  '/integration/smart-on-fhir/launch',
  // Experimental OAuth consent surface for remote MCP clients. It must be
  // reachable before Heidi login so the existing Identity Broker flow can
  // return the user to the pending PKCE authorization request.
  '/mcp/connect',
  // Unauthenticated users land here to start the demo/onboarding flow.
  '/onboarding',
  // Patient pre-consult link: the patient has no Heidi account, and the page
  // runs its own last name and DOB check.
  '/patient-sessions/pre-consult',
  '/shared',
  // Public shared Evidence chat links — page handles auth vs. anonymous internally.
  '/s/',
  // On-prem Keycloak OIDC callback (Tauri desktop flow, MOD-885 T4). A
  // pre-auth page by definition — anyone reaching it is mid-sign-in.
  // Listed here defensively so the middleware Keycloak gate
  // (`if (isKeycloak)` in middleware.ts) can never bounce the callback
  // to the login page, even if the matcher is later extended to include
  // `/onprem/*` paths.
  '/onprem/login-callback',
  // Legal documents — public pages, no auth required.
  // On-prem: these are bundled locally so they work without internet,
  // but middleware still gates them. Listing as public prevents the
  // 401 → logout → redirect loop when token is revoked.
  '/terms-of-use',
  '/usage-policy',
];

export const LOGOUT_ROUTES = ['/kinde-logout', '/desktop-logout'];
